/** The Georgian Lari symbol. */
export const GEL_SYMBOL = '₾';

type FormatGelOptions = {
    /** Prefix the `₾` symbol (default true). */
    withSymbol?: boolean;
};

/**
 * Formats an integer **tetri** amount as a Lari string for display.
 *
 * All money arithmetic happens upstream in integer tetri (1 GEL = 100 tetri);
 * this is the single place a value is turned into text. Grouping is computed on
 * the integer whole part (never on a float) so large sums can't drift.
 *
 * Output: `₾ 1,234.56` — symbol, space, grouped thousands, always 2 decimals.
 * Negatives take a leading minus and **no parentheses** (`₾ -1,234.56`); the
 * grid renders them in the danger colour. `-0` normalises to `₾ 0.00`.
 */
export const formatGel = (
    tetri: number,
    { withSymbol = true }: FormatGelOptions = {},
): string => {
    // Defensive: the contract is integer tetri, but round any stray fraction to
    // the nearest tetri so the 2-dp output is always exact.
    const rounded = Math.round(tetri);
    const negative = rounded < 0;
    const abs = Math.abs(rounded);

    const whole = Math.floor(abs / 100);
    const frac = abs % 100;

    // Integer grouping is exact and locale-stable (comma thousands, dot decimal
    // to match the design regardless of the app's active locale).
    const groupedWhole = whole.toLocaleString('en-US');
    const body = `${negative ? '-' : ''}${groupedWhole}.${frac
        .toString()
        .padStart(2, '0')}`;

    return withSymbol ? `${GEL_SYMBOL} ${body}` : body;
};
